import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import Input from 'components/final-form/Input';
import HideActionButton from 'components/HideActionButton';
import { ReportDiagnosisDtoWithId } from 'components/patient/upsert/UpsertPhysiotherapyReportModal';
import SaveActionButton from 'components/SaveActionButton';
import moment from 'moment';
import React, { useState } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Table } from 'semantic-ui-react';
import { ReportDiagnosisDto } from 'ts-types/api.types';
import { generateUUID } from 'utils/generatorUtils';

interface Props extends MessageSourceProps {
  diagnoses: ReportDiagnosisDtoWithId[],
  addDiagnose: boolean,
  addDiagnoseIx?: number,
  changeDiagnoses: (diagnoses: ReportDiagnosisDtoWithId[]) => void,
  changeSelectedDiagnoses: (selectedDiagnoses: ReportDiagnosisDtoWithId[]) => void,
  changeAddEditDiagnose: (addDiagnose: boolean) => void,
  changeAddEditDiagnoseIx: (index?: number) => void,
  formChange: (field: any, value: any) => void
}

const ReportDiagnosisComponent = (props: Props) => {

  const {
    diagnoses,
    addDiagnose,
    addDiagnoseIx,
    changeDiagnoses,
    changeSelectedDiagnoses,
    changeAddEditDiagnose,
    changeAddEditDiagnoseIx,
    formChange,
    getMessage
  } = props;

  const [editedDiagnose, setEditedDiagnose] =
      useState(undefined as ReportDiagnosisDto | undefined);

  const selectHeaderColumn = () => {

    const countSelectableDiagnosis = diagnoses.length;

    const countSelectedDiagnosis =
        diagnoses.filter(d => d.selected).length;

    const indeterminate =
        countSelectedDiagnosis > 0 && countSelectedDiagnosis < countSelectableDiagnosis;

    const noSelectedDiagnosis =
        countSelectedDiagnosis === 0 && countSelectableDiagnosis > 0;

    const allDiagnosisSelected =
        countSelectableDiagnosis > 0 && countSelectedDiagnosis === countSelectableDiagnosis;

    return (
        <CheckboxSc
            checked={countSelectedDiagnosis > 0}
            indeterminate={indeterminate}
            onChange={() => {

              let selectedDiagnosis: ReportDiagnosisDtoWithId[] = [];
              let allDiagnosis: ReportDiagnosisDtoWithId[] = [];

              if (noSelectedDiagnosis || indeterminate) {

                selectedDiagnosis = diagnoses.map(d => {
                  return {...d, selected: true};
                });
                allDiagnosis = [...selectedDiagnosis];
              }

              if (allDiagnosisSelected) {

                allDiagnosis = diagnoses.map(d => {
                  return {...d, selected: false};
                });
                selectedDiagnosis = [];
              }

              changeDiagnoses(allDiagnosis);
              changeSelectedDiagnoses(selectedDiagnosis);

              formChange("diagnosis", allDiagnosis);
            }
            }

            disabled={countSelectableDiagnosis === 0}
        />
    )
  };

  const selectDiagnosis = (diagnose: ReportDiagnosisDto, index: number) => {

    const modifiedDiagnoses = [...diagnoses];

    if (diagnoses[index]) {
      diagnoses[index].selected = !diagnoses[index].selected;

      formChange(`diagnosis[${index}].selected`, diagnoses[index].selected);

      modifiedDiagnoses[index] = diagnoses[index];
      changeDiagnoses(modifiedDiagnoses);
    }

    const selectedDiagnoses = modifiedDiagnoses.filter(d => d.selected);
    changeSelectedDiagnoses(selectedDiagnoses);

    // formChange("diagnosis", selectedDiagnoses);
  }

  const saveDiagnose = (diagnose: Partial<ReportDiagnosisDtoWithId>, index: number) => {

    const newDiagnose: ReportDiagnosisDtoWithId = {
      id: diagnose.id || generateUUID(),
      treatmentDate: diagnose.treatmentDate || moment().format("YYYY-MM-DD"),
      code: diagnose.code || "",
      description: diagnose.description || "",
      selected: diagnose.selected != undefined ? diagnose.selected : false,
      editable: true,
      hidden: false
    }

    let modifiedDiagnosis;

    if (editedDiagnose) {
      diagnoses[index] = {...newDiagnose}
      modifiedDiagnosis = [...diagnoses];
    } else {
      modifiedDiagnosis = [...diagnoses, newDiagnose];
    }

    const modifiedSelectedDiagnosis = modifiedDiagnosis.filter(d => d.selected);

    changeDiagnoses(modifiedDiagnosis);
    changeSelectedDiagnoses(modifiedSelectedDiagnosis);

    changeAddEditDiagnose(false);
    changeAddEditDiagnoseIx(undefined);
  }

  const cancelDiagnose = (fields: any, index: number) => {

    changeAddEditDiagnose(false);
    changeAddEditDiagnoseIx(undefined);

    if (diagnoses[index] === undefined) {
      fields.remove(index);
    } else {
      fields.update(index, editedDiagnose);
      setEditedDiagnose(undefined);
    }
  };

  const editDiagnose = (diagnose: ReportDiagnosisDto, fields: any, index: number) => {

    // revert any currently unsaved diagnosis
    if (editedDiagnose && addDiagnoseIx !== undefined) {
      if (diagnoses[addDiagnoseIx] !== undefined) {
        fields.update(addDiagnoseIx, editedDiagnose);
      }
    }

    // edit the newly selected diagnosos for editing
    setEditedDiagnose({...diagnose});
    changeAddEditDiagnose(true);
    changeAddEditDiagnoseIx(index);
  }

  const hideOrRemoveDiagnose = (fields: any, index: number, hide: boolean) => {
    let modifiedDiagnoses = [...diagnoses];

    if (!hide) {
      modifiedDiagnoses[index].selected = false;

      modifiedDiagnoses = diagnoses.filter((di, diIndex) => diIndex != index);

      fields.remove(index);
    }

    if (hide) {
      modifiedDiagnoses[index].hidden = true;
    }

    const filteredSelectedDiagnoses = modifiedDiagnoses.filter(d => d.selected);

    formChange("diagnosis", modifiedDiagnoses);

    changeDiagnoses(modifiedDiagnoses);
    changeSelectedDiagnoses(filteredSelectedDiagnoses);
  }

  const renderDiagnosisTable = (diagnose: ReportDiagnosisDtoWithId, index: number, fields: any) => {

    return (
        <React.Fragment key={`${diagnose.id}_${index}_table`}>
          {!diagnose.hidden &&
              <Table.Row>
                <Table.Cell>
                  <CheckboxSc
                      checked={diagnose.selected}
                      onChange={() => selectDiagnosis(diagnose, index)}
                  />
                </Table.Cell>
                <Table.Cell>{treatmentDateCellRenderer(diagnose.treatmentDate)}</Table.Cell>
                <Table.Cell>{diagnose.code}</Table.Cell>
                <Table.Cell>{diagnose.description}</Table.Cell>
                <Table.Cell>{actionsCellRenderer(diagnose, fields, index)}</Table.Cell>
              </Table.Row>
          }
        </React.Fragment>
    );
  };

  const renderDiagnosisForm = (
      field: string,
      diagnose: Partial<ReportDiagnosisDtoWithId>,
      index: number,
      fields: any) => {

    return (
        <Table.Row key={`${diagnose.id}_${index}_form`}>
          <Table.Cell>
            <CheckboxSc
                disabled
                checked={diagnose.selected}
            />
          </Table.Cell>
          <Table.Cell>
            <Field
                name={`${field}.treatmentDate`}
                component={DatePicker}
                fluid
            />
          </Table.Cell>
          <Table.Cell>
            <Field
                name={`${field}.code`}
                component={Input}
                fluid
            />
          </Table.Cell>
          <Table.Cell>
            <Field
                name={`${field}.description`}
                component={Input}
                fluid
            />
          </Table.Cell>
          <Table.Cell>
            <SaveActionButton hoverMessage={getMessage('button.save')}
                              onConfirm={() => saveDiagnose(diagnose, index)}/>
            <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                onConfirm={() => cancelDiagnose(fields, index)}/>
          </Table.Cell>
        </Table.Row>
    );
  }

  const treatmentDateCellRenderer = (cellData: any) => {

    if (moment(cellData).isValid()) {
      return <div>{moment(cellData).format("DD.MM.YYYY")}</div>
    }

    return "";
  }

  const actionsCellRenderer = (rowData: ReportDiagnosisDto, fields: any, index: number) => {

    return (
        <div>
          {
            rowData.editable
                ? <>
                  <EditActionButton
                      hoverMessage={getMessage('msgEdit')}
                      onConfirm={() => editDiagnose(rowData, fields, index)}
                  />
                  <DeleteActionButton
                      popupMessage={getMessage("report.action.deleteDiagnose.confirmMessage")}
                      hoverMessage={getMessage("report.action.deleteDiagnose.hoverMessage")}
                      onConfirm={() => hideOrRemoveDiagnose(fields, index, false)}
                  />
                </>
                : <HideActionButton
                    popupMessage={getMessage("report.action.hideDiagnose.confirmMessage")}
                    hoverMessage={getMessage("report.action.hideDiagnose.hoverMessage")}
                    onConfirm={() => hideOrRemoveDiagnose(fields, index, true)}
                />
          }
        </div>
    );
  }

  return (
      <Table celled selectable size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{selectHeaderColumn()}</Table.HeaderCell>
            <Table.HeaderCell>{getMessage('date')}</Table.HeaderCell>
            <Table.HeaderCell>{getMessage('code')}</Table.HeaderCell>
            <Table.HeaderCell>{getMessage('description')}</Table.HeaderCell>
            <Table.HeaderCell>{getMessage('actions')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <FieldArray name="diagnosis">
            {({fields}) => {
              return fields.map((field, index: number) => {
                if (fields.value[index] !== undefined) {
                  return (
                      (addDiagnose && addDiagnoseIx === index)
                          ? renderDiagnosisForm(field, fields.value[index], index, fields)
                          : renderDiagnosisTable(fields.value[index], index, fields)
                  )
                }
              });
            }
            }
          </FieldArray>
        </Table.Body>
      </Table>
  );

}

export default withMessages(ReportDiagnosisComponent);
